import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  SparklesIcon,
  CreditCardIcon,
  BanknotesIcon,
  DocumentCheckIcon,
  BoltIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

const WhyFlynSection: React.FC = () => {
  const { t } = useLanguage();

  const features = [
    {
      icon: SparklesIcon,
      title: t('why.ai_intelligence'),
      description: t('why.ai_description'),
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      icon: CreditCardIcon,
      title: t('why.smart_cards'),
      description: t('why.smart_cards_description'),
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      icon: BanknotesIcon,
      title: t('why.banking_connection'),
      description: t('why.banking_description'),
      gradient: 'from-green-500 to-emerald-500',
    },
    {
      icon: DocumentCheckIcon,
      title: t('why.mudad_support'),
      description: t('why.mudad_description'),
      gradient: 'from-yellow-500 to-orange-500',
    },
    {
      icon: BoltIcon,
      title: t('why.instant_financing'),
      description: t('why.financing_description'),
      gradient: 'from-red-500 to-pink-500',
    },
    {
      icon: UserGroupIcon,
      title: t('why.partnerships'),
      description: t('why.partnerships_description'),
      gradient: 'from-indigo-500 to-purple-500',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <section id="why-flyn" className="py-20 bg-white dark:bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            {t('why.title')}
          </h2>
          <div className="w-24 h-1 bg-gradient-primary mx-auto rounded-full"></div>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.2 }
              }}
              className="group relative"
            >
              <div className="glass-card p-8 h-full hover:shadow-2xl transition-all duration-300 border border-gray-200/50 dark:border-gray-700/50">
                {/* Icon */}
                <div className="relative mb-6">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${feature.gradient} p-4 shadow-lg group-hover:shadow-xl transition-shadow duration-300`}>
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  
                  {/* Glow Effect */}
                  <div className={`absolute inset-0 w-16 h-16 rounded-2xl bg-gradient-to-r ${feature.gradient} opacity-20 group-hover:opacity-40 transition-opacity duration-300 blur-xl`}></div>
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">
                  {feature.title}
                </h3>
                
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  {feature.description}
                </p>

                {/* Hover Border Effect */}
                <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-primary-200 dark:group-hover:border-primary-800 transition-colors duration-300"></div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16"
        >
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
            اكتشف كيف يمكن لـ FLYN تحويل إدارة أموال شركتك
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-4 bg-gradient-primary text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
          >
            جرب المنصة مجاناً
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyFlynSection;
